import { useState } from 'react';
import { invoicesApi, ordersApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Send, Download, CheckCircle2, Undo2, Loader2, AlertTriangle, MailWarning, Link2, RefreshCw } from 'lucide-react';

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

export function InvoicePanel({ order, onUpdate }) {
  const [busy, setBusy] = useState('');
  const inv = order.invoice;
  const quotePending = order.delivery_method === 'delivery' && order.delivery_fee == null;
  const paid = inv?.payment_status === 'paid';
  const voided = inv?.payment_status === 'void';
  const cancelled = order.status === 'cancelled';
  const num = order.order_number;

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const res = await fn(); if (okMsg) toast.success(okMsg); onUpdate(); return res; }
    catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); }
    finally { setBusy(''); }
  };

  const send = async () => {
    setBusy('send');
    try {
      const { data } = await invoicesApi.send(order.id);
      const s = data.email_status;
      if (s === 'sent') toast.success(`Invoice ${data.order.invoice.number} emailed to ${order.email}`);
      else if (s === 'no_email') toast.warning(`Invoice ${data.order.invoice.number} created — this order has no email, download the PDF to send it`);
      else if (s === 'not_configured') toast.warning(`Invoice ${data.order.invoice.number} created — email isn't set up yet, download the PDF to send it manually`);
      else toast.error(`Invoice created but email failed: ${s.replace('failed: ', '')}`);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send invoice'); }
    finally { setBusy(''); }
  };

  const download = () => run('download', async () => {
    const { data } = await invoicesApi.downloadPdf(order.id);
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  });

  return (
    <div className={`p-3 rounded-md border ${paid ? 'bg-green-50 border-green-200' : voided ? 'bg-muted/40' : inv ? 'bg-blue-50 border-blue-200' : 'bg-secondary/20'}`} data-testid={`invoice-panel-${num}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="font-medium flex items-center gap-1.5"><FileText className="h-4 w-4" /> Invoice</p>
        {inv && (
          <Badge className={paid ? 'bg-green-100 text-green-800' : voided ? 'bg-gray-200 text-gray-700' : 'bg-orange-100 text-orange-800'} data-testid={`invoice-status-${num}`}>{paid ? 'Paid' : voided ? 'Void' : 'Unpaid'}</Badge>
        )}
      </div>

      {order.previous_invoices?.length > 0 && (
        <p className="text-xs text-muted-foreground mb-2" data-testid={`previous-invoices-${num}`}>Previously {order.previous_invoices.map((p) => `${p.number} (void)`).join(', ')}</p>
      )}

      {voided ? (
        <>
          <p className="text-xs mb-2" data-testid={`invoice-void-note-${num}`}>
            <span className="font-semibold">{inv.number}</span> · {fmt(inv.amount)} was voided {inv.voided_at?.slice(0, 10)} — {inv.void_reason || 'cancelled'}.
            {inv.xero?.invoice_id && <span className="text-muted-foreground"> Xero {inv.xero.status === 'VOIDED' ? 'invoice voided too' : `status: ${inv.xero.status?.toLowerCase() || 'unknown'}`}.</span>}
            {inv.xero?.error && <span className="text-red-700"> Xero: {inv.xero.error}</span>}
          </p>
          <p className="text-xs text-muted-foreground mb-2">{cancelled ? 'Change the order status to confirmed or scheduled to rebook it, then send a new invoice.' : `This order is rebooked — send a new invoice for ${fmt(order.total)} to ${order.email}.`}</p>
          <div className="flex flex-wrap gap-2">
            {!cancelled && (
              <Button size="sm" onClick={send} disabled={busy === 'send' || quotePending} data-testid={`send-new-invoice-${num}`}>
                {busy === 'send' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send new invoice
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={download} disabled={busy === 'download'} data-testid={`download-invoice-${num}`}><Download className="h-4 w-4 mr-1" /> Void PDF</Button>
            {inv.xero?.error && (
              <Button size="sm" variant="outline" onClick={() => run('xero', () => ordersApi.xeroSync(order.id), 'Voided in Xero')} disabled={busy === 'xero'} data-testid={`xero-retry-${num}`}><RefreshCw className="h-4 w-4 mr-1" /> Retry Xero</Button>
            )}
          </div>
        </>
      ) : !inv ? (
        <>
          <p className="text-xs text-muted-foreground mb-2">
            {cancelled ? 'This order is cancelled — no invoice will be sent.' : quotePending ? 'Set the delivery fee first so the invoice total is right.' : `Emails a PDF invoice for ${fmt(order.total)} to ${order.email}, due before the ${order.delivery_method === 'pickup' ? 'pickup' : 'delivery'} date.`}
          </p>
          <Button size="sm" onClick={send} disabled={busy === 'send' || quotePending || cancelled} data-testid={`send-invoice-${num}`}>
            {busy === 'send' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send invoice
          </Button>
        </>
      ) : (
        <>
          <div className="text-xs space-y-0.5 mb-3" data-testid={`invoice-details-${num}`}>
            <p><span className="font-semibold">{inv.number}</span> · {fmt(inv.amount)} · issued {inv.issued_at?.slice(0, 10)}</p>
            <p>Due <span className="font-medium">{inv.due_date}</span>{inv.due_before_delivery ? ' (before delivery)' : ''}</p>
            {paid && <p className="text-green-800 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Paid {inv.paid_at?.slice(0, 10)} via {inv.payment_method === 'card' ? 'card (Stripe)' : (inv.payment_method || 'bank transfer').replace('_', ' ')}</p>}
            {inv.email_status === 'sent' && <p className="text-muted-foreground">Emailed {inv.sent_count > 1 ? `${inv.sent_count}× ` : ''}· last {inv.sent_at?.slice(0, 10)}</p>}
            {inv.email_status === 'not_configured' && <p className="text-amber-700 flex items-center gap-1" data-testid={`invoice-email-warning-${num}`}><MailWarning className="h-3 w-3" /> Not emailed — email sending isn't set up yet. Download and send manually.</p>}
            {inv.email_status === 'no_email' && <p className="text-amber-700 flex items-center gap-1" data-testid={`invoice-email-warning-${num}`}><MailWarning className="h-3 w-3" /> No customer email on this order — download the PDF to send it.</p>}
            {inv.xero && (
              inv.xero.error
                ? <p className="text-red-700 flex items-center gap-1" data-testid={`xero-error-${num}`}><AlertTriangle className="h-3 w-3" /> Xero: {inv.xero.error}</p>
                : <p className="text-muted-foreground flex items-center gap-1" data-testid={`xero-synced-${num}`}><Link2 className="h-3 w-3" /> Xero {inv.xero.invoice_number || ''} · {inv.xero.status?.toLowerCase()}{inv.xero.amount_due > 0 ? ` · ${fmt(inv.xero.amount_due)} due` : ''}</p>
            )}
            {inv.email_status?.startsWith('failed') && <p className="text-red-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Email failed: {inv.email_status.replace('failed: ', '')}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={download} disabled={busy === 'download'} data-testid={`download-invoice-${num}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
            {!paid && (
              <>
                <Button size="sm" variant="outline" onClick={send} disabled={busy === 'send'} data-testid={`resend-invoice-${num}`}>{busy === 'send' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Resend</Button>
                <Button size="sm" onClick={() => run('paid', () => invoicesApi.markPaid(order.id, { paid: true, payment_method: 'bank_transfer' }), 'Marked as paid')} disabled={busy === 'paid'} data-testid={`mark-paid-${num}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid</Button>
              </>
            )}
            {inv.xero?.error && (
              <Button size="sm" variant="outline" onClick={() => run('xero', () => ordersApi.xeroSync(order.id), 'Synced to Xero')} disabled={busy === 'xero'} data-testid={`xero-retry-${num}`}>{busy === 'xero' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Retry Xero</Button>
            )}
            {paid && inv.payment_method !== 'card' && (
              <Button size="sm" variant="ghost" onClick={() => run('unpaid', () => invoicesApi.markPaid(order.id, { paid: false }), inv.xero?.invoice_id ? 'Marked as unpaid — remove the payment in Xero manually' : 'Marked as unpaid')} disabled={busy === 'unpaid'} data-testid={`mark-unpaid-${num}`}><Undo2 className="h-4 w-4 mr-1" /> Undo</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
